import { useEffect, useRef } from "react"
import Icon from "@/v2-ui/icon"
import Markdown from "@/v2-backoffice-client/components/markdown"
import type { LayerAiChatTurn } from "@/v2-backoffice-client/layer/ai/layer.ai.handoff"

export type LayerAiThreadProps = {
  turns: LayerAiChatTurn[]
  /** Renders the thinking indicator after the last message. */
  isThinking: boolean
}

/**
 * The conversation thread of the AI assistant: user messages right-aligned,
 * assistant messages as markdown. Follows the conversation like a chat —
 * the newest message (and the thinking indicator) stays in view whenever
 * the thread grows.
 */
function LayerAiThread(props: LayerAiThreadProps) {
  const { turns, isThinking } = props

  const threadRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight })
  }, [turns, isThinking])

  return (
    // Edge-to-edge on the panel background (the panel body carries no
    // padding; it lives inside the scroll container) so the scrollbar hugs
    // the panel border — same surface treatment as the editor area.
    <div
      ref={threadRef}
      className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-2"
    >
      {turns.map((turn, index) =>
        turn.role === "user" ? (
          <div
            key={index}
            className="max-w-[85%] self-end whitespace-pre-wrap rounded-lg bg-primary/10 px-2.5 py-1.5 text-xs"
          >
            {turn.content}
          </div>
        ) : turn.isError ? (
          <div
            key={index}
            className="flex items-start gap-2 self-stretch rounded-lg border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-xs"
          >
            <Icon
              src="triangle-warning"
              size={12}
              className="mt-0.5 shrink-0 text-destructive"
            />
            <span className="min-w-0">
              <span className="whitespace-pre-wrap text-destructive">
                {turn.content}
              </span>
              <span className="mt-1 block text-muted-foreground">
                The conversation is intact — send your message again to retry.
              </span>
            </span>
          </div>
        ) : (
          <Markdown
            key={index}
            className="self-stretch rounded-lg bg-muted/50 px-2.5 py-2 text-muted-foreground"
          >
            {turn.content}
          </Markdown>
        ),
      )}
      {isThinking && (
        <div className="flex max-w-[85%] items-center gap-2 self-start rounded-md bg-muted p-2 text-xs text-muted-foreground">
          <span className="inline-block size-3 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Thinking — this can take up to a minute…
        </div>
      )}
    </div>
  )
}

export default LayerAiThread
